import React, { useCallback, useState } from 'react';
import { TrendingUp, Cpu, HardDrive, Activity, Clock, CheckCircle, AlertTriangle, Server, Loader2, Lightbulb, BarChart3 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { authenticatedFetch } from '../utils/api';
import { API_HOST } from '../utils/constants';
import { useSequentialPolling } from '../hooks/useSequentialPolling';

/**
 * 管理员监控页面
 * 路由: /admin/monitor
 * 显示完整的系统监控数据,不对普通用户暴露
 */
const AdminMonitor = () => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  const [metrics, setMetrics] = useState({
    queue: {
      queueSize: 0,
      activeThreads: 0,
      completedTasks: 0,
      poolSize: 0,
      estimatedWaitTime: 0,
    },
    system: {
      totalMemory: 0,
      freeMemory: 0,
      usedMemory: 0,
      maxMemory: 0,
      memoryUsagePercent: 0,
      availableProcessors: 0,
      systemLoadAverage: 0,
    },
  });

  const [history, setHistory] = useState({
    queueSize: [],
    activeThreads: [],
    memoryUsage: [],
    systemLoad: [],
    timestamps: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = useCallback(async ({ signal } = {}) => {
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/metrics/status`, {
        signal,
        timeout: 45_000,
      });
      if (signal?.aborted) return;
      setMetrics(data);
      setError(null);
      setIsLoading(false);

      // 更新历史数据（保留最近 60 个数据点 = 2分钟）
      setHistory((prev) => ({
        queueSize: [...prev.queueSize, data.queue.queueSize].slice(-60),
        activeThreads: [...prev.activeThreads, data.queue.activeThreads].slice(-60),
        memoryUsage: [...prev.memoryUsage, data.system.memoryUsagePercent].slice(-60),
        systemLoad: [...prev.systemLoad, data.system.systemLoadAverage].slice(-60),
        timestamps: [...prev.timestamps, new Date().toLocaleTimeString()].slice(-60),
      }));
    } catch (err) {
      if (signal?.aborted) return;
      console.error('[AdminMonitor] Failed to fetch metrics:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, []);

  // 每轮完成 2 秒后再请求；页面隐藏时暂停。
  useSequentialPolling(fetchMetrics, 2000);

  const formatBytes = (bytes) => {
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb.toFixed(2)} GB`;
  };

  const getStatusColor = (percent) => {
    if (percent < 50) return 'var(--color-success)';
    if (percent < 80) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const getPressureLevel = () => {
    const { queueSize, poolSize } = metrics.queue;
    if (queueSize === 0) return { level: 'idle', text: '空闲', color: theme.colors.success };
    if (queueSize < poolSize * 0.5) return { level: 'low', text: '负载正常', color: theme.colors.success };
    if (queueSize < poolSize) return { level: 'medium', text: '负载较高', color: theme.colors.warning };
    return { level: 'high', text: '负载很高', color: theme.colors.error };
  };

  const pressure = getPressureLevel();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ backgroundColor: theme.colors.bgPrimary }}>
        <div className="text-center">
          <Loader2 size={48} className="animate-spin mx-auto mb-4" style={{ color: theme.colors.accentPrimary }} />
          <p style={{ color: theme.colors.textSecondary }}>加载监控数据...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ backgroundColor: theme.colors.bgPrimary }}>
        <div className="text-center">
          <AlertTriangle size={48} className="mx-auto mb-4" style={{ color: theme.colors.error }} />
          <p style={{ color: theme.colors.textPrimary }}>监控数据加载失败</p>
          <p className="text-sm mt-2" style={{ color: theme.colors.textMuted }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: theme.colors.bgPrimary }}>
      {/* 页面标题 */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Server size={32} style={{ color: theme.colors.accentPrimary }} />
          <h1 className="text-3xl font-bold" style={{ color: theme.colors.textPrimary }}>
            系统监控中心
          </h1>
        </div>
        <p className="text-sm" style={{ color: theme.colors.textMuted }}>
          实时监控服务器性能和任务队列状态 · 每2秒自动刷新
        </p>
      </div>

      {/* 压力指示器 */}
      <div
        className="mb-6 p-4 rounded-lg flex items-center gap-3"
        style={{ backgroundColor: `${pressure.color}15` }}
      >
        <div
          className="w-3 h-3 rounded-full animate-pulse"
          style={{ backgroundColor: pressure.color }}
        />
        <span className="font-semibold" style={{ color: pressure.color }}>
          系统状态: {pressure.text}
        </span>
        {metrics.queue.queueSize > 0 && (
          <span className="text-sm" style={{ color: theme.colors.textSecondary }}>
            · 预计等待 {Math.floor(metrics.queue.estimatedWaitTime / 60)} 分钟
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 左侧：任务队列 */}
        <div>
          {/* 队列指标卡片 */}
          <div className="mb-6">
            <h2
              className="text-lg font-semibold mb-4 flex items-center gap-2"
              style={{ color: theme.colors.textPrimary }}
            >
              <TrendingUp size={20} />
              任务队列
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <MetricCard
                label="处理中"
                value={`${metrics.queue.activeThreads} / ${metrics.queue.poolSize}`}
                icon={<Activity size={20} />}
                color={theme.colors.accentPrimary}
                theme={theme}
              />
              <MetricCard
                label="排队中"
                value={metrics.queue.queueSize}
                icon={<Clock size={20} />}
                color={getStatusColor((metrics.queue.queueSize / metrics.queue.poolSize) * 100)}
                theme={theme}
              />
              <MetricCard
                label="今日完成"
                value={metrics.queue.completedTasks}
                icon={<CheckCircle size={20} />}
                color={theme.colors.success}
                theme={theme}
              />
              <MetricCard
                label="预计等待"
                value={`${Math.floor(metrics.queue.estimatedWaitTime / 60)}分钟`}
                icon={<Clock size={20} />}
                color={theme.colors.textSecondary}
                theme={theme}
              />
            </div>
          </div>

          {/* 队列趋势图 */}
          <div
            className="p-4 rounded-lg"
            style={{ backgroundColor: theme.colors.bgSecondary }}
          >
            <h3 className="text-sm font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>
              队列趋势（最近2分钟）
            </h3>
            <div className="space-y-4">
              <LargeChart
                label="队列长度"
                data={history.queueSize}
                color={theme.colors.accentPrimary}
                theme={theme}
              />
              <LargeChart
                label="活跃线程"
                data={history.activeThreads}
                color={theme.colors.success}
                theme={theme}
              />
            </div>
          </div>
        </div>

        {/* 右侧：系统资源 */}
        <div>
          {/* 系统资源卡片 */}
          <div className="mb-6">
            <h2
              className="text-lg font-semibold mb-4 flex items-center gap-2"
              style={{ color: theme.colors.textPrimary }}
            >
              <Cpu size={20} />
              系统资源
            </h2>

            {/* 内存使用 */}
            <div
              className="p-4 rounded-lg mb-4"
              style={{ backgroundColor: theme.colors.bgSecondary }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
                  内存使用
                </span>
                <span className="text-sm font-bold" style={{ color: theme.colors.textPrimary }}>
                  {formatBytes(metrics.system.usedMemory)} / {formatBytes(metrics.system.maxMemory)}
                  <span style={{ color: getStatusColor(metrics.system.memoryUsagePercent) }}>
                    {' '}({metrics.system.memoryUsagePercent.toFixed(1)}%)
                  </span>
                </span>
              </div>
              <div
                className="h-3 rounded-full overflow-hidden"
                style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
              >
                <div
                  className="h-full transition-all duration-300"
                  style={{
                    width: `${metrics.system.memoryUsagePercent}%`,
                    backgroundColor: getStatusColor(metrics.system.memoryUsagePercent),
                  }}
                />
              </div>
            </div>

            {/* CPU 信息 */}
            <div className="grid grid-cols-2 gap-4">
              <MetricCard
                label="CPU 核心数"
                value={metrics.system.availableProcessors}
                icon={<Cpu size={20} />}
                color={theme.colors.textSecondary}
                theme={theme}
              />
              <MetricCard
                label="系统负载"
                value={metrics.system.systemLoadAverage.toFixed(2)}
                icon={<Activity size={20} />}
                color={getStatusColor((metrics.system.systemLoadAverage / metrics.system.availableProcessors) * 100)}
                theme={theme}
              />
            </div>
          </div>

          {/* 系统资源趋势图 */}
          <div
            className="p-4 rounded-lg"
            style={{ backgroundColor: theme.colors.bgSecondary }}
          >
            <h3 className="text-sm font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>
              资源趋势（最近2分钟）
            </h3>
            <div className="space-y-4">
              <LargeChart
                label="内存使用 (%)"
                data={history.memoryUsage}
                color={theme.colors.warning}
                theme={theme}
              />
              <LargeChart
                label="系统负载"
                data={history.systemLoad}
                color={theme.colors.error}
                theme={theme}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 底部说明 */}
      <div
        className="mt-6 p-4 rounded-lg text-xs"
        style={{ backgroundColor: theme.colors.bgSecondary, color: theme.colors.textMuted }}
      >
        <p className="flex items-center gap-1.5"><Lightbulb size={13} className="shrink-0" /> 提示: 此页面仅供管理员使用,不对普通用户开放。数据每2秒自动刷新。</p>
        <p className="mt-1 flex items-center gap-1.5"><BarChart3 size={13} className="shrink-0" /> 历史数据保留最近2分钟(60个数据点),更长期的监控记录功能开发中...</p>
      </div>
    </div>
  );
};

// 指标卡片组件
const MetricCard = ({ label, value, icon, color, theme }) => (
  <div
    className="p-4 rounded-lg"
    style={{ backgroundColor: theme.colors.bgSecondary }}
  >
    <div className="flex items-center gap-2 mb-2">
      <div style={{ color }}>{icon}</div>
      <div className="text-xs" style={{ color: theme.colors.textMuted }}>
        {label}
      </div>
    </div>
    <div className="font-mono-num text-2xl font-bold" style={{ color }}>
      {value}
    </div>
  </div>
);

// 大图表组件
const LargeChart = ({ label, data, color, theme }) => {
  const max = Math.max(...data, 1);
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - (value / max) * 80; // 留20%上边距
    return `${x},${y}`;
  }).join(' ');

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium" style={{ color: theme.colors.textSecondary }}>
          {label}
        </span>
        <span className="font-mono-num text-sm font-bold" style={{ color }}>
          {data[data.length - 1]?.toFixed(1) || 0}
        </span>
      </div>
      <svg
        viewBox="0 0 100 100"
        className="w-full h-24 rounded"
        style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
      >
        {/* 网格线 */}
        <line x1="0" y1="25" x2="100" y2="25" stroke={theme.colors.borderSecondary} strokeWidth="0.5" opacity="0.3" />
        <line x1="0" y1="50" x2="100" y2="50" stroke={theme.colors.borderSecondary} strokeWidth="0.5" opacity="0.3" />
        <line x1="0" y1="75" x2="100" y2="75" stroke={theme.colors.borderSecondary} strokeWidth="0.5" opacity="0.3" />

        {/* 数据线 */}
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />

        {/* 填充区域 */}
        <polyline
          points={`0,100 ${points} 100,100`}
          fill={color}
          opacity="0.1"
        />
      </svg>
    </div>
  );
};

export default AdminMonitor;
